/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetContext } from './widget-context-base';
import { iamLogin } from '../main';
import { iamLocalization } from '../localization';
import { WidgetElements } from '../elements/widget-elements-base';
import { iam } from '../iam';

export class PrimaryAuthWidgetContext extends WidgetContext {
  constructor(elements: WidgetElements) {
    super(elements);
  }

  public override localize() {
    iamLogin.localizeElement('sub-header', 'passwordSubhead');
    iamLogin.localizeElement('sub-head-link', { stringKey: 'notUserSubhead', stringParameters: { email: window.localStorage.getItem('login_hint') } });
    iamLogin.localizeElement(this.elements.getPasswordLabel(), { stringKey: 'password', isWidgetElement: true });
    iamLogin.localizeElement(this.elements.getSubmitButton(), { stringKey: 'next', isWidgetElement: true });
    iamLogin.localizeElement('needHelpSigningIn', 'needHelpSigningIn');
  }

  public override initialize() {
    // main login.html page (password entry)
    if (iam.settings.debug) console.log('initializing password widget');
    document.getElementById('dont-have-a-mvision').style.display = iam.settings.displaySignup ? '' : 'none';
    // hide username field since they entered email on prior page
    const usernameFieldset = this.elements.getUsernameFieldset();
    if (usernameFieldset) {
      usernameFieldset.style.display = 'none';
    }
    this.elements.getPasswordField().setAttribute('data-testid', 'password-field'); // for testability
    this.elements.getSubmitButton().setAttribute('data-testid', 'submit-button'); // for testability
    iamLogin.showWidget();
    if (iamLogin.isChrome()) {
      // chrome won't return a password string until there's a click in the page, so watch for an autofill indicator for a while
      let intervalCount = 0;
      const interval = setInterval(() => {
        if (this.elements.getPasswordAutofilledField()) {
          iamLogin.page.validateForm();
          // once autofill has been detected, no more need to keep watching
          if (iam.settings.debug) console.log('chrome password field autofill detected');
          clearInterval(interval);
        } else if (intervalCount >= 100) {
          // give up after 5 seconds
          if (iam.settings.debug) console.log('chrome hasn\'t autofilled the password field');
          clearInterval(interval);
        }
        intervalCount++;
      }, 50);
    }
    // show 'not foo@bar.com?' link and footer
    iamLogin.page.showCommonFields(['sub-head-link', 'post-okta']);
    iamLogin.page.initializeCommon();

    if (document.forms.length) {
      // apply to the last form because when the widget transitions between contexts, the new one will be added before the old one is removed
      document.forms[document.forms.length - 1].addEventListener('submit', this.onPasswordSubmit, true);
    }
  }

  public override setInitialFocus() {
    const field = this.elements.getPasswordField();
    if (field && !field.disabled) {
      field.focus();
    }
  }

  public override validateForm() {
    // disable submit button if the password field is blank
    this.elements.getSubmitButton().disabled = !(this.elements.getPasswordField().value.length > 0 ||
      (iamLogin.isChrome() && this.elements.getPasswordAutofilledField())); // chrome won't return a password string until there's a click in the page, so this handles that case
  }

  private onPasswordSubmit(e: SubmitEvent) {
    if (iamLogin.sessionIsActive()) {
      iamLogin.showTransitioningWidgetPage();
      iam.Token.setLocation(iam.settings.alreadySignedIn + '?error=' + encodeURIComponent(iamLocalization.localize('alreadyLoggedInError')));
      // another session already exists, so don't try to create a 2nd
      e.stopImmediatePropagation(); // since the submit won't take place, prevent other submit handlers from running
      e.preventDefault();
      return false;
    } else {
      return true;
    }
  }
}
